// DONM3/services/frontend/src/components/orgChart.js

import { OrgChart } from 'd3-org-chart'
import { DataTransform } from '../utils/dataTransform.js'
import { ModalComponent } from './modal.js'

export class OrgChartComponent {
  constructor(selector, data) {
    this.selector = selector
    this.rawData = data
    this.chart = null
    this.transformedData = null
    this.searchQuery = ''
    this.searchTimeout = null
    this.isRendering = false
    this.modal = new ModalComponent();
  }

  render() {
    const container = document.querySelector(this.selector)
    
    try {
      container.innerHTML = ''
      
      this.transformedData = DataTransform.hierarchyToOrgChart(this.rawData)
      
      if (!this.transformedData || this.transformedData.length === 0) {
        throw new Error('Нет данных для отображения')
      }

      console.log('Создаем org-chart с новым дизайном:', this.transformedData.length, 'узлов')

      this.chart = new OrgChart()
        .container(this.selector)
        .data(this.transformedData)
        .nodeWidth(() => 280)
        .nodeHeight(() => 140)
        .childrenMargin(() => 50)
        .compactMarginBetween(() => 25)
        .neighbourMargin(() => 100)
        .siblingsMargin(() => 25)
        .duration(250)
        .buttonContent(({ node, state }) => {
          const colors = this.getNodeColors(node.depth)
          return `<div style="
            background-color: ${colors.main};
            color: white;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            cursor: pointer;
            user-select: none;
            border: 1px solid rgba(255,255,255,0.5);
          ">
            <span>${node.children ? (state.expanded ? '−' : '+') : ''}</span>
          </div>`
        })
        .nodeContent((d) => this.createNewNodeContent(d))
        .onNodeClick((d) => this.onNodeClick(d))
        .render()

      // Плавное разворачивание
      setTimeout(() => {
        if (this.chart) {
          this.chart.expandAll().render()
          setTimeout(() => this.chart && this.chart.fit(), 300)
        }
      }, 150)

      console.log('OrgChart с новым дизайном отрендерен')
      
    } catch (error) {
      console.error('Ошибка рендеринга org-chart:', error.stack)
      container.innerHTML = `<div class="error"><h3>⚠️ Ошибка</h3><p>${error.message}</p></div>`
    }
  }

  getNodeColors(depth) {
    const palettes = {
      0: { main: '#007BFF', gradient: 'linear-gradient(45deg, #007BFF, #00C6FF)' }, // Руководитель - Синий
      1: { main: '#28a745', gradient: 'linear-gradient(45deg, #28a745, #20c997)' }, // 1-й уровень - Зеленый
      2: { main: '#6f42c1', gradient: 'linear-gradient(45deg, #6f42c1, #a985e6)' }, // 2-й уровень - Фиолетовый
      3: { main: '#fd7e14', gradient: 'linear-gradient(45deg, #fd7e14, #ffc107)' }, // 3-й уровень - Оранжевый
      default: { main: '#6c757d', gradient: 'linear-gradient(45deg, #6c757d, #adb5bd)' } // Остальные - Серый
    }
    return palettes[depth] || palettes.default
  }

  createNewNodeContent(d) {
    const node = d.data
    const colors = this.getNodeColors(d.depth)
    
    return `
      <div class="node-card" style="
        --accent-gradient: ${colors.gradient};
        --accent-color: ${colors.main};
      ">
        <!-- Левая цветная рамка -->
        <div class="node-accent-border"></div>
        
        <!-- Аватар -->
        <div class="node-avatar" style="background-image: url('${node.imageurl}')">
          ${!node.imageurl ? `<span>${this.getInitials(node.name)}</span>` : ''}
        </div>
        
        <!-- Контент -->
        <div class="node-content">
          <div class="node-name">${node.name || 'Не указано'}</div>
          <div class="node-position">${node.position || 'Должность не указана'}</div>
          
          ${node.department ? `<div class="node-department">
            <span>📍</span> ${node.department}
          </div>` : ''}
        </div>
        
        <!-- Счетчик подчиненных -->
        ${d.children && d.children.length > 0 ? `
          <div class="node-children-count">
            👥 ${d.children.length}
          </div>
        ` : ''}
      </div>
    `
  }

  getInitials(name) {
    if (!name) return '?'
    return name
      .split(' ')
      .slice(0, 2)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
  }

  onNodeClick(d) {
    console.log('Клик по узлу:', d.data.name, 'Уровень:', d.depth)
    // Открываем модальное окно с информацией о сотруднике
    this.modal.openModal(d.data, this.getNodeColors(d.depth));
  }

  search(query) {
    if (!this.chart) return
    
    if (this.searchTimeout) clearTimeout(this.searchTimeout)
    
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query)
    }, 300)
  }

  performSearch(query) {
    this.searchQuery = query.toLowerCase().trim()
    
    if (!this.searchQuery) {
      this.chart.clearHighlighting()
      return
    }

    const matchingIds = this.transformedData
      .filter(node => 
        node.name.toLowerCase().includes(this.searchQuery) ||
        (node.position && node.position.toLowerCase().includes(this.searchQuery)) ||
        (node.department && node.department.toLowerCase().includes(this.searchQuery))
      )
      .map(node => node.id)

    this.chart.setHighlighted(matchingIds).render()
  }

  expandAll() {
    if (!this.chart || this.isRendering) return
    this.isRendering = true
    this.chart.expandAll().render()
    setTimeout(() => { this.isRendering = false }, 500)
  }

  collapseAll() {
    if (!this.chart || this.isRendering) return
    this.isRendering = true
    this.chart.collapseAll().render()
    setTimeout(() => { this.isRendering = false }, 500)
  }

  resetZoom() {
    if (!this.chart) return
    this.chart.fit()
  }
}
